import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { Banner } from '@/components/basicComponents/banner';
import FileUpload from '@/components/basicComponents/fileUpload';
import { FormFooter } from '@/components/basicComponents/formFooter';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import RadioButton from '@/components/basicComponents/radioButton';
import Textarea from '@/components/basicComponents/textarea';
import { DropFileArea } from '@/components/dueDiligence-page/dueDiligenceRecord/dropFileArea';
import FileNotSupported from '@/components/dueDiligence-page/modals/fileNotSupported';
import Label from '@/components/basicComponents/label';
import Header from '@/components/header';
import { GoBackButton } from '@/components/header/goBackButton';
import { PageTitle } from '@/components/header/pageTitle';
import { SettingsContainer, SettingsSection } from '@/components/settings-page';
import { getCacheBustedFileUrl } from '@/components/settings-page/utils';
import { useErrorMessages } from '@/hooks/i18n';
import { useAuth } from '@/hooks/use-auth';
import { useResponsive } from '@/hooks/use-responsive';
import { isValidUrl } from '@/shared/utils/url';
import { MainWrap } from '@/styles/common';
import { themeVar } from '@/styles/theme';
import axios from 'axios';
import { ChangeEvent, useEffect, useState } from 'react';
import styled from 'styled-components';
import { fileExtensions } from '../constants';
import { IFirmSettings, IFontStyle } from '../types';

const radioButtonItems = ['None', 'Text', 'Image'];
const getTemplateStylingWatermarkFilePath = (tenantId: string) => `logos/${tenantId}/template-styling-watermark`;

interface EditWatermarkProps {
  currentRow: IFirmSettings | null;
  saveSettingValue: (key: string, value: string | IFontStyle) => Promise<void>;
  handleClose: () => void;
}

export const EditWatermark = ({ currentRow, saveSettingValue, handleClose }: EditWatermarkProps) => {
  const { user } = useAuth();
  const { isMobile, isTablet } = useResponsive();
  const { default: defaultErrorMessage } = useErrorMessages();

  const [selectedOption, setSelectedOption] = useState(radioButtonItems[0]);
  const [watermarkText, setWatermarkText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [drag, setDrag] = useState(false);
  const [modalWindow, setModalWindow] = useState({ isOpen: false, type: 'not-supported' });
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFileToDelete, setIsFileToDelete] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!currentRow?.value) {
      setSelectedOption(radioButtonItems[0]);
      return;
    }
    if (isValidUrl(currentRow?.value)) {
      setFileUrl(currentRow.value);
      setSelectedOption(radioButtonItems[2]);
      return;
    }
    if (currentRow?.value !== 'None') {
      setWatermarkText(currentRow?.value);
      setSelectedOption(radioButtonItems[1]);
      return;
    }
    setSelectedOption(radioButtonItems[0]);

    return () => {
      setWatermarkText('');
    };
  }, [currentRow]);

  const handleRadioChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSelectedOption(event.target.value);
  };

  const onDropHandler = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    const uploadFiles = e.dataTransfer.files;
    setDrag(false);

    if (fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const handleUploadFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const uploadFiles = e.target.files;

    if (uploadFiles && fileExtensions.includes(uploadFiles[0]?.name.split('.').pop()?.toUpperCase() ?? '')) {
      setFile(uploadFiles[0]);
      setFileUrl(URL.createObjectURL(uploadFiles[0]));
      return;
    }
    setModalWindow({ isOpen: true, type: 'not-supported' });
  };

  const deletePreviewItem = () => {
    setFile(null);
    setFileUrl('');
    if (currentRow?.value && isValidUrl(currentRow.value)) {
      setIsFileToDelete(true);
    }
  };

  const deleteAttachment = async () => {
    const currentFileExtension = currentRow?.value?.split('.')?.pop()?.toLowerCase();

    await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
      data: {
        filePath: `${getTemplateStylingWatermarkFilePath(user?.tenant?.id)}.${currentFileExtension}`
      },
      headers: {
        authorization: `Bearer ${localStorage.getItem('token')}`
      }
    });

    await saveSettingValue(currentRow?.key, '');
  };

  const handleLogoUpload = async (file: File) => {
    if (file) {
      const currentFileExtension = isValidUrl(currentRow?.value) ? currentRow?.value?.split('.')?.pop()?.toLowerCase() : '';
      const newFileExtension = file?.name?.split('.')?.pop()?.toLowerCase();

      if (isValidUrl(currentRow?.value) && currentFileExtension !== newFileExtension) {
        await axios.delete(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, {
          data: {
            filePath: `${getTemplateStylingWatermarkFilePath(user?.tenant?.id)}.${currentFileExtension}`
          },
          headers: {
            authorization: `Bearer ${localStorage.getItem('token')}`
          }
        });
      }

      const formData = new FormData();
      formData.append('file', file);
      formData.append('filePath', `${getTemplateStylingWatermarkFilePath(user?.tenant?.id)}.${newFileExtension}`);
      formData.append('metadata', JSON.stringify({}));

      const { data } = await axios.put(`${process.env.REACT_APP_BACKEND_BASE_URL}/unsecure-storage/file`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      await saveSettingValue(currentRow?.key, data?.url ?? '');
    }
  };

  const saveValue = async () => {
    try {
      setErrorMessage(null);
      setLoading(true);

      switch (selectedOption) {
        case 'None':
        case 'Text':
          if (currentRow?.value && isValidUrl(currentRow.value)) {
            await deleteAttachment();
          }

          const value = selectedOption === 'None' ? 'None' : watermarkText;
          await saveSettingValue(currentRow?.key, value);
          break;
        case 'Image':
          if (isFileToDelete && !file) {
            await deleteAttachment();
          }

          if (file) {
            await handleLogoUpload(file);
          }
          break;
        default:
          break;
      }

      handleClose();
    } catch (error) {
      setErrorMessage(defaultErrorMessage);
    } finally {
      setLoading(false);
    }
  };

  const openLogoImage = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      {loading && (
        <ModalWrappedContent
          isOpen={loading}
          title="Saving"
          showCancelButton={false}
          showCloseIcon={false}
          isUseOutsideClick={false}
          onClose={() => {}}
        >
          <ModalLoader />
        </ModalWrappedContent>
      )}
      {modalWindow.isOpen && modalWindow.type === 'not-supported' && (
        <FileNotSupported isOpen={modalWindow.isOpen} onClose={() => setModalWindow({ ...modalWindow, isOpen: false })} />
      )}
      <MainWrap>
        <Header modalControl={<GoBackButton handleClose={handleClose} backToTitle="Templates" />} />
        <PageTitle title={currentRow?.fieldName ?? ''} />
      </MainWrap>
      <MainWrap>
        {errorMessage && (
          <Banner
            icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
            title="Error"
            description={errorMessage}
            bgColor={themeVar('context.error')}
          />
        )}
        <SettingsContainer>
          <SettingsSection isMobile={isMobile}>
            <Label size="lg">Watermark Type</Label>
            <RadioButtonsWrap>
              {radioButtonItems.map((item) => (
                <RadioButton
                  key={item}
                  name="watermark"
                  radioButtonText={item}
                  selectedOption={selectedOption}
                  handleRadioChange={handleRadioChange}
                />
              ))}
            </RadioButtonsWrap>
            {selectedOption === 'None' && <Label size="md">Watermark None</Label>}
            {selectedOption === 'Text' && (
              <WatermarkTextareaWrapper isTablet={isTablet} isMobile={isMobile}>
                <Textarea
                  label="Watermark Text"
                  value={watermarkText}
                  onChange={(e) => setWatermarkText(e.target.value)}
                  limitCharactersTo={80}
                  size="md"
                  autoFocus
                />
              </WatermarkTextareaWrapper>
            )}
            {selectedOption === 'Image' && (
              <>
                {fileUrl && (
                  <FileUpload
                    fileName="Watermark"
                    imageSrc={getCacheBustedFileUrl(fileUrl)}
                    type="image"
                    openFile={() => openLogoImage(getCacheBustedFileUrl(fileUrl))}
                    deleteFile={deletePreviewItem}
                  />
                )}
                <DropFileArea
                  id="watermark"
                  fileExtensions={fileExtensions}
                  handleUploadFile={handleUploadFile}
                  onDropHandler={onDropHandler}
                  drag={drag}
                  setDrag={setDrag}
                />
              </>
            )}
          </SettingsSection>
          <FormFooter onCancel={handleClose} onSave={saveValue} disableSaveButton={loading} showRequiredFieldsWarning={false} />
        </SettingsContainer>
      </MainWrap>
    </>
  );
};

const RadioButtonsWrap = styled.label`
  display: flex;
`;

const WatermarkTextareaWrapper = styled.div<{ isTablet: boolean; isMobile: boolean }>`
  width: ${({ isMobile, isTablet }) => (isMobile || isTablet ? '100%' : '50%')};
`;
